import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BookOpen, Sparkles, Send, Package, CheckCircle2, Clock, XCircle, Upload, Fingerprint, Loader2, Lightbulb, Banknote } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Markdown } from "@/components/devhub/markdown";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const CATEGORIES = ["sci-fi", "fantasy", "modern-civilian", "military", "horror", "cyberpunk", "historical", "stylized"];

const STATUS_META = {
  submitted: { cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10", icon: Clock, label: "In QA review" },
  approved: { cls: "text-nc-green border-nc-green/40 bg-nc-green/10", icon: CheckCircle2, label: "Approved" },
  rejected: { cls: "text-red-400 border-red-500/40 bg-red-500/10", icon: XCircle, label: "Changes requested" },
};

export default function FabPacksTab() {
  const [guide, setGuide] = useState(null);
  const [locked, setLocked] = useState("");
  const [sub, setSub] = useState("guide"); // guide | ideas | submit | mine
  const [ideas, setIdeas] = useState(null);
  const [ideaCategory, setIdeaCategory] = useState("");
  const [ideaBusy, setIdeaBusy] = useState(false);
  const [mine, setMine] = useState(null);
  const [sending, setSending] = useState(false);
  const thumbRef = useRef(null);
  const previewsRef = useRef(null);

  const [form, setForm] = useState({
    title: "", concept: "", category: "sci-fi", ai_tools_used: "", delivery_link: "",
    unique_meshes: "", skeletal_meshes: "", static_meshes: "", material_count: "",
    material_instance_count: "", texture_count: "", texture_resolutions: "",
    triangles_assembled: "", lod_count: "0", animation_count: "0",
    separate_parts: false, epic_skeleton: false, nanite_enabled: false,
    ip_consent: false, accuracy_consent: false,
  });
  const [thumb, setThumb] = useState(null);
  const [previews, setPreviews] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadGuide = useCallback(() => {
    // Check identity first so locked devs don't generate a 403 on every DevHub load
    api.get("/onboarding/identity/status")
      .then((s) => {
        if (s.data.id_status !== "verified" || !s.data.has_document) {
          setLocked("The Fab character-pack pipeline is only open to identity-verified contractors. Complete Identity Verification in Dev Hub → Onboarding first.");
          return;
        }
        api.get("/dev/fab/guide")
          .then((r) => { setGuide(r.data); setLocked(""); })
          .catch((e) => setLocked(formatApiErrorDetail(e.response?.data?.detail) || "Verification required"));
      })
      .catch(() => setLocked("Verification required"));
  }, []);

  const loadMine = useCallback(() => {
    api.get("/dev/fab/submissions/mine").then((r) => setMine(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadGuide(); loadMine(); }, [loadGuide, loadMine]);

  const genIdeas = async () => {
    setIdeaBusy(true);
    try {
      const r = await api.post("/dev/fab/ideas", { category: ideaCategory });
      setIdeas(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setIdeaBusy(false);
    }
  };

  const applyIdea = (idea) => {
    setForm((f) => ({ ...f, title: idea.title, concept: `${idea.concept}\n\nWhy it sells: ${idea.why_it_sells}\nTarget buyer: ${idea.target_buyer} // Suggested band: ${idea.price_band}`, category: idea.category }));
    setSub("submit");
    toast.success("Idea loaded into the submission form");
  };

  const submit = async () => {
    if (!thumb) { toast.error("Thumbnail image is required"); return; }
    if (previews.length !== 7) { toast.error(`Exactly 7 preview images required (got ${previews.length})`); return; }
    setSending(true);
    try {
      const fd = new FormData();
      for (const [k, v] of Object.entries(form)) fd.append(k, typeof v === "boolean" ? String(v) : v);
      fd.append("ue_version", "5.1");
      fd.append("thumbnail", thumb);
      previews.forEach((f) => fd.append("previews", f));
      const r = await api.post("/dev/fab/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.message);
      setForm((f) => ({ ...f, title: "", concept: "", ai_tools_used: "", delivery_link: "" }));
      setThumb(null); setPreviews([]);
      loadMine();
      setSub("mine");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (locked) {
    return (
      <div data-testid="fab-lock" className="max-w-2xl border border-nc-orange/30 bg-nc-orange/5 p-8 flex items-start gap-4">
        <Fingerprint className="w-6 h-6 text-nc-orange shrink-0 mt-1" />
        <div>
          <p className="font-sans font-bold uppercase text-base text-zinc-50">Fab Pack Pipeline — verified contractors only</p>
          <p className="font-mono text-[11px] text-zinc-400 mt-2 leading-relaxed">{locked}</p>
          <p className="font-mono text-[10px] text-nc-orange mt-3 uppercase tracking-[0.2em]">Complete Onboarding → Identity Verification to unlock.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="fab-tab" className="flex flex-col gap-6 max-w-4xl">
      <div className="border border-white/10 bg-black/40 p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">Fab.com Character Packs</p>
          <p className="font-sans font-bold text-xl text-zinc-50 mt-1">AUD 10.00 per approved pack — full IP transfers to Specpixel</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[["guide", "Pack Bible", BookOpen], ["ideas", "Idea Generator", Lightbulb], ["submit", "Submit Pack", Send], ["mine", `My Packs${mine ? ` (${mine.submissions.length})` : ""}`, Package]].map(([t, label, Icon]) => (
            <button key={t} data-testid={`fab-sub-${t}`} onClick={() => setSub(t)} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors flex items-center gap-1.5 ${sub === t ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {sub === "guide" && (
        <motion.section data-testid="fab-guide" className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
          {guide ? <Markdown>{guide.markdown}</Markdown> : <p className="font-mono text-xs text-zinc-500 animate-pulse uppercase tracking-[0.25em]">Loading the Pack Bible…</p>}
        </motion.section>
      )}

      {sub === "ideas" && (
        <motion.section data-testid="fab-ideas" className="border border-white/10 bg-black/40 p-6 flex flex-col gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
          <div>
            <h3 className="font-sans font-bold uppercase text-base text-zinc-50 flex items-center gap-2"><Sparkles className="w-4 h-4 text-nc-orange" /> Sellable Pack Idea Generator</h3>
            <p className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">AI-scored ideas tuned to what actually sells on Fab — pick a winner instead of guessing. Building off a scored idea massively raises your approval odds.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <select data-testid="fab-idea-category" className={`${inputCls} max-w-[220px]`} value={ideaCategory} onChange={(e) => setIdeaCategory(e.target.value)}>
              <option value="">All hottest categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button data-testid="fab-ideas-generate" disabled={ideaBusy} onClick={genIdeas} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 flex items-center gap-2 transition-colors">
              {ideaBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {ideaBusy ? "Analysing market…" : "Generate ideas"}
            </button>
          </div>
          {ideas && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ideas.ideas.map((idea, i) => (
                <div key={i} data-testid={`fab-idea-card-${i + 1}`} className="border border-white/10 bg-[#050505]/60 p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-sans font-bold text-sm text-zinc-100">{idea.title}</p>
                    <span data-testid={`fab-idea-score-${i + 1}`} className={`font-mono text-[10px] border px-2 py-0.5 ${idea.sellability >= 85 ? "text-nc-green border-nc-green/40 bg-nc-green/10" : "text-nc-orange border-nc-orange/40 bg-nc-orange/10"}`}>{idea.sellability}/100</span>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{idea.concept}</p>
                  <p className="font-mono text-[10px] text-nc-blue leading-relaxed">{idea.why_it_sells}</p>
                  <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.15em]">{idea.category} // {idea.target_buyer} // {idea.price_band}</p>
                  <button data-testid={`fab-idea-use-${i + 1}`} onClick={() => applyIdea(idea)} className="self-start mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 hover:bg-nc-orange/10 px-3 py-1.5 transition-colors">
                    Build this pack →
                  </button>
                </div>
              ))}
              {ideas.source === "fallback" && <p className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em] md:col-span-2">AI scorer offline — showing curated best-sellers instead.</p>}
            </div>
          )}
        </motion.section>
      )}

      {sub === "submit" && (
        <motion.section data-testid="fab-submit" className="border border-white/10 bg-black/40 p-6 flex flex-col gap-5" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
          <div>
            <h3 className="font-sans font-bold uppercase text-base text-zinc-50">Submit a completed pack</h3>
            <p className="font-mono text-[10px] text-zinc-500 mt-1">Every answer below is copied straight into the Fab listing — count in the editor, don't guess. Wrong numbers = rejection.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Pack title *</label><input data-testid="fab-f-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Cyberpunk Street Enforcer" /></div>
            <div><label className={labelCls}>Category *</label>
              <select data-testid="fab-f-category" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          </div>
          <div><label className={labelCls}>Concept / description * (min 20 chars)</label><textarea data-testid="fab-f-concept" rows={3} className={inputCls} value={form.concept} onChange={(e) => set("concept", e.target.value)} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>AI tools used * (e.g. Tripo, Meshy 7)</label><input data-testid="fab-f-tools" className={inputCls} value={form.ai_tools_used} onChange={(e) => set("ai_tools_used", e.target.value)} /></div>
            <div><label className={labelCls}>Delivery link * (Google Drive / Dropbox — packaged UE5.1 project)</label><input data-testid="fab-f-link" className={inputCls} value={form.delivery_link} onChange={(e) => set("delivery_link", e.target.value)} placeholder="https://…" /></div>
          </div>

          <div className="border border-white/10 bg-[#050505]/50 p-4">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase mb-3">Production gates — all must be true (UE 5.1 is enforced automatically)</p>
            {[["separate_parts", "Head, upper body and lower body were generated SEPARATELY (Tripo/Meshy) and assembled"], ["epic_skeleton", "Rigged to the UE5 Epic Skeleton (Manny/Quinn bone names) — retarget test passed"], ["nanite_enabled", "Nanite enabled on all applicable static meshes"]].map(([k, label]) => (
              <label key={k} className="flex items-start gap-3 cursor-pointer mb-2.5">
                <input data-testid={`fab-f-${k}`} type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="mt-0.5 accent-[#2E6BFF]" />
                <span className="font-mono text-[10px] text-zinc-300 leading-relaxed">{label}</span>
              </label>
            ))}
          </div>

          <div className="border border-white/10 bg-[#050505]/50 p-4">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase mb-3">Fab technical questionnaire — exact counts from the editor</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[["unique_meshes", "Unique meshes *"], ["skeletal_meshes", "Skeletal meshes *"], ["static_meshes", "Static meshes"], ["material_count", "Master materials *"], ["material_instance_count", "Material instances"], ["texture_count", "Textures *"], ["triangles_assembled", "Tris (assembled char) *"], ["lod_count", "LODs (0 if none)"], ["animation_count", "Animations (0 if none)"]].map(([k, label]) => (
                <div key={k}><label className={labelCls}>{label}</label><input data-testid={`fab-f-${k}`} type="number" min="0" className={inputCls} value={form[k]} onChange={(e) => set(k, e.target.value)} /></div>
              ))}
            </div>
            <div className="mt-4"><label className={labelCls}>Texture resolutions * (e.g. "7× 2048, 3× 4096")</label><input data-testid="fab-f-texture_resolutions" className={inputCls} value={form.texture_resolutions} onChange={(e) => set("texture_resolutions", e.target.value)} /></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Thumbnail * (1 image — the real model, front 3/4)</label>
              <button data-testid="fab-thumb-button" onClick={() => thumbRef.current?.click()} className="border border-dashed border-white/20 hover:border-nc-blue px-4 py-4 w-full font-mono text-[11px] text-zinc-400 hover:text-nc-blue transition-colors flex items-center justify-center gap-2">
                <Upload className="w-4 h-4" /> {thumb ? thumb.name : "Choose thumbnail…"}
              </button>
              <input ref={thumbRef} data-testid="fab-thumb-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setThumb(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className={labelCls}>Preview images * (exactly 7 — honest shots of the real models)</label>
              <button data-testid="fab-previews-button" onClick={() => previewsRef.current?.click()} className="border border-dashed border-white/20 hover:border-nc-blue px-4 py-4 w-full font-mono text-[11px] text-zinc-400 hover:text-nc-blue transition-colors flex items-center justify-center gap-2">
                <Upload className="w-4 h-4" /> {previews.length ? `${previews.length} selected` : "Choose 7 previews…"}
              </button>
              <input ref={previewsRef} data-testid="fab-previews-input" type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setPreviews(Array.from(e.target.files || []).slice(0, 7))} />
            </div>
          </div>

          <div className="border border-nc-orange/30 bg-nc-orange/5 p-4 flex flex-col gap-3">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">Legal declarations — recorded with timestamp + IP</p>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="fab-f-ip_consent" type="checkbox" checked={form.ip_consent} onChange={(e) => set("ip_consent", e.target.checked)} className="mt-1 accent-[#C74A00]" />
              <span className="font-mono text-[10px] text-zinc-300 leading-relaxed">I irrevocably assign <b>100% of all intellectual property and commercial rights</b> in this pack to Specpixel Entertainment PTY LTD. The pack becomes Specpixel's sole property to sell, modify, rebrand and distribute forever. I will not resell, share or reuse it. It is my original AI-assisted work — no copyrighted characters, brands or likenesses.</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input data-testid="fab-f-accuracy_consent" type="checkbox" checked={form.accuracy_consent} onChange={(e) => set("accuracy_consent", e.target.checked)} className="mt-1 accent-[#C74A00]" />
              <span className="font-mono text-[10px] text-zinc-300 leading-relaxed">I declare the thumbnail and all 7 previews <b>accurately represent the actual models</b> — AI enhancement was used for composition/backdrop only, never to make the assets look better than they are. No false advertising.</span>
            </label>
          </div>

          <button data-testid="fab-submit-button" onClick={submit} disabled={sending || !form.ip_consent || !form.accuracy_consent || !form.separate_parts || !form.epic_skeleton || !form.nanite_enabled} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 flex items-center gap-2 transition-colors">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {sending ? "Uploading pack…" : "Submit for QA — AUD 10.00 on approval"}
          </button>
        </motion.section>
      )}

      {sub === "mine" && (
        <motion.section data-testid="fab-mine" className="flex flex-col gap-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
          {mine && mine.earnings.length > 0 && (
            <div data-testid="fab-earnings" className="border border-nc-green/30 bg-nc-green/5 p-4 flex items-center gap-3">
              <Banknote className="w-5 h-5 text-nc-green shrink-0" />
              <p className="font-mono text-[11px] text-zinc-300">
                Bounties: <span className="text-nc-green font-bold">AUD {mine.earnings.filter((e) => e.status === "claimable").reduce((s, e) => s + e.amount, 0).toFixed(2)} claimable</span>
                {" · "}AUD {mine.earnings.filter((e) => e.status === "paid").reduce((s, e) => s + e.amount, 0).toFixed(2)} paid — the studio processes payouts manually after approval.
              </p>
            </div>
          )}
          {(!mine || mine.submissions.length === 0) && <p data-testid="fab-mine-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No packs submitted yet — generate an idea and build your first.</p>}
          {(mine?.submissions || []).map((p, i) => {
            const sm = STATUS_META[p.status] || STATUS_META.submitted;
            return (
              <div key={p.id} data-testid={`fab-pack-${i + 1}`} className="border border-white/10 bg-black/40 p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-nc-blue shrink-0" />
                    <p className="font-sans font-bold text-sm text-zinc-100">{p.title}</p>
                    <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 flex items-center gap-1 ${sm.cls}`}><sm.icon className="w-3 h-3" /> {sm.label}</span>
                  </div>
                  <p className="font-mono text-[10px] text-zinc-500">{p.category} // {String(p.created_at).slice(0, 10)} // bounty AUD {Number(p.bounty_aud).toFixed(2)}</p>
                </div>
                {p.status === "rejected" && p.review_notes && <p data-testid={`fab-pack-notes-${i + 1}`} className="font-mono text-[10px] text-red-300 mt-2 border-l-2 border-red-500/50 pl-3">QA notes: {p.review_notes}</p>}
                {p.status === "approved" && <p className="font-mono text-[10px] text-nc-green mt-2">AUD {Number(p.bounty_aud).toFixed(2)} bounty credited — pack is now Specpixel property heading to Fab.</p>}
              </div>
            );
          })}
        </motion.section>
      )}
    </div>
  );
}
